import { reflect } from '@effector/reflect';
import { Anchor } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { useUnit } from 'effector-react';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GameSettingWithId } from 'shared/api';
import { translated } from 'shared/lib';
import { accountRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import { GameSettingRegistryModel } from '../../model';
import cls from './game-setting-registry.module.scss';

export type GameSettingRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	emptyActionSlot?: ReactNode;
	filterSlot?: ReactNode;
	tableTdActionSlot?: (gameSetting: GameSettingWithId) => ReactNode;
};

export const createGameSettingRegistry = ({
	model,
	$currencies,
	$settings,
}: GameSettingRegistryModel) => {
	const Registry = createRegistry(model);

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			label: translated('game-setting-registry.filters.currencies.label'),
			placeholder: translated('game-setting-registry.filters.currencies.placeholder'),
			className: cls.gameSettingRegistryCurrenciesFilter,
			data: $currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
		},
	});

	const GameSettingRegistry = forwardRef<HTMLDivElement, GameSettingRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				emptyActionSlot,
				filterSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			const settings = useUnit($settings);

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('game-setting-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.gameSettingRegistryTopBar}>
						<Registry.Filters className={cls.gameSettingRegistryFilters}>
							{filterSlot}
							<CurrenciesFilter />
						</Registry.Filters>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							{withAccount && <Registry.Table.Col />}
							<Registry.Table.Col />
							{settings.map((setting) => (
								<Registry.Table.Col key={setting.code} />
							))}
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								{withAccount && (
									<Registry.Table.Th>
										{t('game-setting-registry.table-columns.accounts')}
									</Registry.Table.Th>
								)}
								<Registry.Table.Th>
									{t('game-setting-registry.table-columns.currency')}
								</Registry.Table.Th>
								{settings.map((setting) => (
									<Registry.Table.Th key={setting.code}>
										{t(`game-setting.${setting.code}`)}
									</Registry.Table.Th>
								))}
								<Registry.Table.Th>
									{t('game-setting-registry.table-columns.actions')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										{withTitle && (
											<Registry.Table.Td>
												<Anchor
													component={Link<StoreValue<typeof accountRoute.$params>>}
													to={accountRoute}
													query={{ id: row.account.id }}
												>
													{row.account.name}
												</Anchor>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										{settings.map((setting) => {
											const rowSetting = row.settings.find((s) => s.code === setting.code);

											let cellValue: string | number = ' ';
											if (rowSetting) {
												if (rowSetting.type === 'array') {
													cellValue = (rowSetting.value as number[]).join(', ');
												} else {
													cellValue = `${rowSetting.value}`;
												}
											}

											return <Registry.Table.Td key={setting.code}>{cellValue}</Registry.Table.Td>;
										})}
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('game-setting-registry.empty.title')}
						message={t('game-setting-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('game-setting-registry.table-empty.title')}
						message={t('game-setting-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return GameSettingRegistry;
};
